<template>
  <div class="enhanced-audio-player">
    <!-- 播放器主体 -->
    <div class="player-main">
      <!-- 播放控制区域 -->
      <div class="player-controls">
        <!-- 播放/暂停按钮 -->
        <button class="play-pause-btn" @click="togglePlay" :title="isPlaying ? '暂停' : '播放'">
          <el-icon v-if="!isPlaying"><Play /></el-icon>
          <el-icon v-else><Pause /></el-icon>
        </button>
        
        <!-- 进度条 -->
        <div class="progress-section">
          <div class="progress-container" @click="seekAudio">
            <div class="progress-background">
              <div class="progress-buffered" :style="{ width: bufferedProgress + '%' }"></div>
              <div class="progress-played" :style="{ width: progress + '%' }"></div>
            </div>
            <div 
              class="progress-handle" 
              :style="{ left: progress + '%' }"
              @mousedown="startDragHandle"
            ></div>
          </div>
          
          <!-- 时间显示 -->
          <div class="time-display">
            <span class="current-time">{{ formatTime(currentTime) }}</span>
            <span class="duration">{{ formatTime(duration) }}</span>
          </div>
        </div>
      </div>
      
      <!-- 额外控制区域 -->
      <div class="extra-controls">
        <!-- 音量控制 -->
        <div class="volume-control">
          <button class="volume-btn" @click="toggleMute" :title="isMuted ? '取消静音' : '静音'">
            <el-icon v-if="!isMuted && volume > 0.5"><Volume /></el-icon>
            <el-icon v-else-if="!isMuted && volume > 0"><VolumeMedium /></el-icon>
            <el-icon v-else><VolumeMuted /></el-icon>
          </button>
          <div class="volume-slider-container">
            <input 
              type="range" 
              min="0" 
              max="1" 
              step="0.01" 
              :value="volume" 
              @input="setVolume"
              class="volume-slider"
              @mousedown="showVolumeSlider = true"
              @mouseup="hideVolumeSliderAfterDelay"
              @mouseleave="hideVolumeSliderAfterDelay"
            />
          </div>
        </div>
        
        <!-- 播放速度控制 -->
        <div class="speed-control">
          <el-select 
            v-model="playbackSpeed" 
            @change="setPlaybackSpeed"
            size="small"
            class="speed-select"
            title="播放速度"
          >
            <el-option label="0.5x" :value="0.5" />
            <el-option label="0.75x" :value="0.75" />
            <el-option label="1x" :value="1" />
            <el-option label="1.25x" :value="1.25" />
            <el-option label="1.5x" :value="1.5" />
            <el-option label="2x" :value="2" />
          </el-select>
        </div>
        
        <!-- 循环播放 -->
        <button class="control-btn" @click="toggleLoop" :class="{ active: loop }" title="循环播放">
          <el-icon><Refresh /></el-icon>
        </button>
        
        <!-- 下载按钮 -->
        <button class="control-btn" @click="downloadAudio" title="下载音频">
          <el-icon><Download /></el-icon>
        </button>
      </div>
    </div>
    
    <!-- 音频波形可视化（可选功能） -->
    <div class="visualizer-container" v-if="showVisualizer">
      <canvas ref="visualizerCanvas" class="visualizer"></canvas>
    </div>
    
    <!-- 加载指示器 -->
    <div class="loading-indicator" v-if="isLoading && !hasError">
      <el-icon class="loading-spinner"><Loading /></el-icon>
    </div>
    
    <!-- 错误提示 -->
    <div class="error-message" v-if="hasError">
      <el-icon class="error-icon"><CircleClose /></el-icon>
      <span>{{ errorMessage }}</span>
      <button class="retry-btn" @click="retryLoading">重试</button>
    </div>
    
    <!-- 隐藏的音频元素 -->
    <audio
      ref="audioElement"
      :src="src"
      @loadedmetadata="onMetadataLoaded"
      @timeupdate="onTimeUpdate"
      @progress="onProgress"
      @error="onAudioError"
      @ended="onPlaybackEnded"
      @playing="onPlaying"
      @waiting="onWaiting"
      @canplay="onCanPlay"
    >
      您的浏览器不支持音频播放
    </audio>
  </div>
</template>

<script>
import { ElIcon, ElSelect, ElOption } from 'element-plus'
import { 
  Play, Pause, Volume, VolumeMedium, VolumeMuted, 
  Refresh, Download, Loading, CircleClose 
} from '@element-plus/icons-vue'

export default {
  name: 'EnhancedAudioPlayer',
  components: {
    ElIcon,
    ElSelect,
    ElOption,
    Play,
    Pause,
    Volume,
    VolumeMedium,
    VolumeMuted,
    Refresh,
    Download,
    Loading,
    CircleClose
  },
  props: {
    src: {
      type: String,
      required: true
    },
    initialVolume: {
      type: Number,
      default: 0.8,
      validator: (value) => value >= 0 && value <= 1
    },
    autoPlay: {
      type: Boolean,
      default: false
    },
    loop: {
      type: Boolean,
      default: false
    },
    showVisualizer: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      volume: this.initialVolume,
      isMuted: false,
      playbackSpeed: 1.0,
      bufferedProgress: 0,
      isLoading: true,
      hasError: false,
      errorMessage: '',
      showVolumeSlider: false,
      isDragging: false,
      visualizerContext: null,
      visualizerAnalyser: null,
      visualizerSource: null,
      visualizerAnimationId: null,
      originalVolume: 1.0,
      volumeSliderTimeout: null
    }
  },
  mounted() {
    this.setupAudioElement()
    if (this.showVisualizer) {
      this.setupVisualizer()
    }
    this.setupGlobalEventListeners()
  },
  beforeUnmount() {
    this.cleanupVisualizer()
    this.cleanupGlobalEventListeners()
    if (this.volumeSliderTimeout) {
      clearTimeout(this.volumeSliderTimeout)
    }
  },
  computed: {
    // 计算当前播放进度百分比
    progress() {
      if (this.duration === 0) return 0
      return (this.currentTime / this.duration) * 100
    }
  },
  methods: {
    // 设置音频元素
    setupAudioElement() {
      if (!this.$refs.audioElement) return
      
      const audio = this.$refs.audioElement
      audio.volume = this.initialVolume
      audio.loop = this.loop
      
      // 自动播放（可能会被浏览器策略阻止）
      if (this.autoPlay) {
        this.togglePlay()
      }
    },
    
    // 设置全局事件监听器
    setupGlobalEventListeners() {
      // 监听鼠标移动，用于拖动进度条
      document.addEventListener('mousemove', this.handleMouseMove)
      document.addEventListener('mouseup', this.handleMouseUp)
      
      // 监听键盘事件
      document.addEventListener('keydown', this.handleKeydown)
    },
    
    // 清理全局事件监听器
    cleanupGlobalEventListeners() {
      document.removeEventListener('mousemove', this.handleMouseMove)
      document.removeEventListener('mouseup', this.handleMouseUp)
      document.removeEventListener('keydown', this.handleKeydown)
    },
    
    // 元数据加载完成
    onMetadataLoaded() {
      if (!this.$refs.audioElement) return
      
      this.duration = this.$refs.audioElement.duration
      this.isLoading = false
    },
    
    // 播放时间更新
    onTimeUpdate() {
      if (!this.$refs.audioElement || this.isDragging) return
      
      this.currentTime = this.$refs.audioElement.currentTime
    },
    
    // 缓冲进度更新
    onProgress() {
      if (!this.$refs.audioElement) return
      
      const audio = this.$refs.audioElement
      const buffered = audio.buffered
      
      if (buffered.length > 0) {
        const bufferedEnd = buffered.end(buffered.length - 1)
        this.bufferedProgress = (bufferedEnd / this.duration) * 100
      }
    },
    
    // 播放开始
    onPlaying() {
      this.isPlaying = true
      this.isLoading = false
      
      if (this.showVisualizer) {
        this.startVisualizer()
      }
    },
    
    // 等待加载
    onWaiting() {
      this.isLoading = true
    },
    
    // 可以播放
    onCanPlay() {
      this.isLoading = false
    },
    
    // 播放结束
    onPlaybackEnded() {
      this.isPlaying = false
      
      if (this.showVisualizer) {
        this.stopVisualizer()
      }
    },
    
    // 音频错误
    onAudioError(event) {
      this.isLoading = false
      this.hasError = true
      
      const audio = event.target
      let errorMsg = '音频加载失败'
      
      switch (audio.error.code) {
        case MediaError.MEDIA_ERR_ABORTED:
          errorMsg = '音频加载被用户中止'
          break
        case MediaError.MEDIA_ERR_NETWORK:
          errorMsg = '网络错误导致音频加载失败'
          break
        case MediaError.MEDIA_ERR_DECODE:
          errorMsg = '音频解码失败'
          break
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
          errorMsg = '音频格式不支持'
          break
      }
      
      this.errorMessage = errorMsg
    },
    
    // 重试加载
    retryLoading() {
      this.hasError = false
      this.isLoading = true
      
      if (this.$refs.audioElement) {
        this.$refs.audioElement.src = ''
        setTimeout(() => {
          this.$refs.audioElement.src = this.src
          this.$refs.audioElement.load()
        }, 100)
      }
    },
    
    // 切换播放/暂停
    togglePlay() {
      if (!this.$refs.audioElement || this.hasError) return
      
      const audio = this.$refs.audioElement
      
      if (audio.paused) {
        audio.play()
          .then(() => {
            this.isPlaying = true
          })
          .catch(error => {
            console.error('播放失败:', error)
            this.errorMessage = '无法自动播放音频，请点击播放器重试'
          })
      } else {
        audio.pause()
        this.isPlaying = false
        
        if (this.showVisualizer) {
          this.stopVisualizer()
        }
      }
    },
    
    // 跳转到指定位置
    seekAudio(event) {
      if (!this.$refs.audioElement || this.isDragging) return
      
      const progressContainer = event.currentTarget
      const rect = progressContainer.getBoundingClientRect()
      const pos = (event.clientX - rect.left) / rect.width
      const newTime = pos * this.duration
      
      this.$refs.audioElement.currentTime = newTime
      this.currentTime = newTime
    },
    
    // 开始拖动进度条
    startDragHandle(event) {
      event.stopPropagation()
      this.isDragging = true
    },
    
    // 处理鼠标移动（拖动进度条）
    handleMouseMove(event) {
      if (!this.isDragging || !this.$refs.audioElement) return
      
      const progressContainer = this.$el.querySelector('.progress-container')
      if (!progressContainer) return
      
      const rect = progressContainer.getBoundingClientRect()
      let pos = (event.clientX - rect.left) / rect.width
      pos = Math.max(0, Math.min(1, pos)) // 限制在0-1范围内
      
      this.currentTime = pos * this.duration
    },
    
    // 处理鼠标释放（结束拖动进度条）
    handleMouseUp() {
      if (!this.isDragging || !this.$refs.audioElement) return
      
      this.isDragging = false
      this.$refs.audioElement.currentTime = this.currentTime
    },
    
    // 切换静音
    toggleMute() {
      if (!this.$refs.audioElement) return
      
      const audio = this.$refs.audioElement
      
      if (this.isMuted) {
        // 取消静音，恢复原来的音量
        audio.volume = this.originalVolume
        this.volume = this.originalVolume
      } else {
        // 静音，保存当前音量
        this.originalVolume = audio.volume
        audio.volume = 0
        this.volume = 0
      }
      
      this.isMuted = !this.isMuted
    },
    
    // 设置音量
    setVolume(event) {
      if (!this.$refs.audioElement) return
      
      const volume = parseFloat(event.target.value)
      this.$refs.audioElement.volume = volume
      this.volume = volume
      this.isMuted = volume === 0
      
      if (volume > 0) {
        this.originalVolume = volume
      }
    },
    
    // 设置播放速度
    setPlaybackSpeed() {
      if (!this.$refs.audioElement) return
      
      this.$refs.audioElement.playbackRate = this.playbackSpeed
    },
    
    // 切换循环播放
    toggleLoop() {
      this.loop = !this.loop
      
      if (this.$refs.audioElement) {
        this.$refs.audioElement.loop = this.loop
      }
    },
    
    // 下载音频
    downloadAudio() {
      const link = document.createElement('a')
      link.href = this.src
      link.download = this.getAudioFileName()
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    
    // 获取音频文件名
    getAudioFileName() {
      const urlParts = this.src.split('/')
      const fileName = urlParts[urlParts.length - 1]
      return fileName
    },
    
    // 延迟隐藏音量滑块
    hideVolumeSliderAfterDelay() {
      if (this.volumeSliderTimeout) {
        clearTimeout(this.volumeSliderTimeout)
      }
      
      this.volumeSliderTimeout = setTimeout(() => {
        this.showVolumeSlider = false
      }, 1500)
    },
    
    // 处理键盘事件
    handleKeydown(event) {
      // 检查是否在输入框中，避免干扰
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return
      }
      
      switch (event.key) {
        case ' ':
        case 'Spacebar': // IE 兼容
          event.preventDefault()
          this.togglePlay()
          break
        case 'ArrowLeft':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault()
            this.seekBackward()
          }
          break
        case 'ArrowRight':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault()
            this.seekForward()
          }
          break
        case 'm':
        case 'M':
          event.preventDefault()
          this.toggleMute()
          break
        case '+':
        case '=':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault()
            this.increaseVolume()
          }
          break
        case '-':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault()
            this.decreaseVolume()
          }
          break
      }
    },
    
    // 快退
    seekBackward(seconds = 10) {
      if (!this.$refs.audioElement) return
      
      const newTime = Math.max(0, this.currentTime - seconds)
      this.$refs.audioElement.currentTime = newTime
      this.currentTime = newTime
    },
    
    // 快进
    seekForward(seconds = 10) {
      if (!this.$refs.audioElement) return
      
      const newTime = Math.min(this.duration, this.currentTime + seconds)
      this.$refs.audioElement.currentTime = newTime
      this.currentTime = newTime
    },
    
    // 增加音量
    increaseVolume() {
      const newVolume = Math.min(1, this.volume + 0.1)
      this.volume = newVolume
      
      if (this.$refs.audioElement) {
        this.$refs.audioElement.volume = newVolume
        this.isMuted = newVolume === 0
        if (newVolume > 0) {
          this.originalVolume = newVolume
        }
      }
    },
    
    // 减少音量
    decreaseVolume() {
      const newVolume = Math.max(0, this.volume - 0.1)
      this.volume = newVolume
      
      if (this.$refs.audioElement) {
        this.$refs.audioElement.volume = newVolume
        this.isMuted = newVolume === 0
        if (newVolume > 0) {
          this.originalVolume = newVolume
        }
      }
    },
    
    // 格式化时间
    formatTime(seconds) {
      if (isNaN(seconds) || seconds === Infinity) return '0:00'
      
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = Math.floor(seconds % 60)
      
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
    },
    
    // 设置音频可视化器
    setupVisualizer() {
      if (!this.$refs.visualizerCanvas) return
      
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext
        const audioContext = new AudioContext()
        const analyser = audioContext.createAnalyser()
        const canvas = this.$refs.visualizerCanvas
        const ctx = canvas.getContext('2d')
        
        // 设置画布大小
        canvas.width = canvas.offsetWidth * window.devicePixelRatio
        canvas.height = canvas.offsetHeight * window.devicePixelRatio
        ctx.scale(window.devicePixelRatio, window.devicePixelRatio)
        
        // 连接音频源
        const source = audioContext.createMediaElementSource(this.$refs.audioElement)
        source.connect(analyser)
        analyser.connect(audioContext.destination)
        
        analyser.fftSize = 256
        
        this.visualizerContext = ctx
        this.visualizerAnalyser = analyser
        this.visualizerSource = source
      } catch (error) {
        console.warn('音频可视化不可用:', error)
        this.showVisualizer = false
      }
    },
    
    // 开始可视化
    startVisualizer() {
      if (!this.visualizerContext || !this.visualizerAnalyser) return
      
      this.drawVisualizer()
    },
    
    // 停止可视化
    stopVisualizer() {
      if (this.visualizerAnimationId) {
        cancelAnimationFrame(this.visualizerAnimationId)
        this.visualizerAnimationId = null
      }
    },
    
    // 绘制可视化效果
    drawVisualizer() {
      if (!this.visualizerContext || !this.visualizerAnalyser) return
      
      const ctx = this.visualizerContext
      const analyser = this.visualizerAnalyser
      const canvas = this.$refs.visualizerCanvas
      const bufferLength = analyser.frequencyBinCount
      const dataArray = new Uint8Array(bufferLength)
      
      // 清除画布
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight)
      
      const draw = () => {
        this.visualizerAnimationId = requestAnimationFrame(draw)
        
        analyser.getByteFrequencyData(dataArray)
        
        // 绘制波形
        const barWidth = (canvas.offsetWidth / bufferLength) * 2.5
        let x = 0
        
        for (let i = 0; i < bufferLength; i++) {
          const barHeight = dataArray[i] / 2
          
          ctx.fillStyle = `rgb(${barHeight + 100}, 200, 255)`
          ctx.fillRect(x, canvas.offsetHeight - barHeight, barWidth, barHeight)
          
          x += barWidth + 1
        }
      }
      
      draw()
    },
    
    // 清理可视化器
    cleanupVisualizer() {
      this.stopVisualizer()
      
      if (this.visualizerSource) {
        this.visualizerSource.disconnect()
      }
      
      this.visualizerContext = null
      this.visualizerAnalyser = null
      this.visualizerSource = null
    }
  }
}
</script>

<style scoped>
.enhanced-audio-player {
  position: relative;
  background-color: #f5f7fa;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.player-main {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.player-controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.play-pause-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #409eff;
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
}

.play-pause-btn:hover {
  background-color: #66b1ff;
  transform: scale(1.05);
}

.play-pause-btn:active {
  transform: scale(0.95);
}

.play-pause-btn i {
  font-size: 20px;
}

.progress-section {
  flex: 1;
  min-width: 0;
}

.progress-container {
  position: relative;
  width: 100%;
  height: 8px;
  background-color: #e0e0e0;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 8px;
}

.progress-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  overflow: hidden;
}

.progress-buffered {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background-color: #bfcbd9;
  transition: width 0.1s linear;
}

.progress-played {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background-color: #409eff;
  transition: width 0.1s linear;
}

.progress-handle {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  background-color: #409eff;
  border: 2px solid white;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: transform 0.1s ease;
  opacity: 0;
}

.progress-container:hover .progress-handle {
  opacity: 1;
}

.progress-handle:hover {
  transform: translate(-50%, -50%) scale(1.2);
}

.time-display {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #606266;
}

.extra-controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.volume-control {
  display: flex;
  align-items: center;
  gap: 8px;
}

.volume-btn {
  background: none;
  border: none;
  color: #606266;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.volume-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
  color: #409eff;
}

.volume-slider-container {
  position: relative;
}

.volume-slider {
  width: 80px;
  height: 4px;
  background-color: #e0e0e0;
  border-radius: 2px;
  outline: none;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s;
}

.volume-control:hover .volume-slider,
.volume-slider:focus,
.show-volume-slider .volume-slider {
  opacity: 1;
}

.volume-slider::-webkit-slider-thumb {
  appearance: none;
  width: 12px;
  height: 12px;
  background-color: #409eff;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.volume-slider::-moz-range-thumb {
  width: 12px;
  height: 12px;
  background-color: #409eff;
  border-radius: 50%;
  cursor: pointer;
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.speed-control {
  margin-left: auto;
}

.speed-select {
  color: #606266;
  background-color: white;
  border-color: #dcdfe6;
}

.control-btn {
  background: none;
  border: none;
  color: #606266;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  width: 36px;
  height: 36px;
}

.control-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
  color: #409eff;
}

.control-btn.active {
  background-color: rgba(64, 158, 255, 0.1);
  color: #409eff;
}

.visualizer-container {
  width: 100%;
  height: 60px;
  margin-top: 16px;
  border-radius: 8px;
  overflow: hidden;
  background-color: rgba(0, 0, 0, 0.05);
}

.visualizer {
  width: 100%;
  height: 100%;
}

.loading-indicator {
  position: absolute;
  top: 50%;
  right: 20px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  color: #409eff;
}

.loading-spinner {
  font-size: 20px;
  animation: spin 1s linear infinite;
}

.error-message {
  position: absolute;
  top: 50%;
  right: 20px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  color: #f56c6c;
  padding: 4px 8px;
  background-color: rgba(245, 108, 108, 0.1);
  border-radius: 4px;
  font-size: 12px;
}

.error-icon {
  font-size: 16px;
}

.retry-btn {
  background: none;
  border: 1px solid #f56c6c;
  color: #f56c6c;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 11px;
  transition: all 0.2s;
}

.retry-btn:hover {
  background-color: #f56c6c;
  color: white;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .player-controls {
    gap: 12px;
  }
  
  .play-pause-btn {
    width: 40px;
    height: 40px;
  }
  
  .play-pause-btn i {
    font-size: 16px;
  }
  
  .extra-controls {
    gap: 8px;
  }
  
  .speed-control {
    display: none;
  }
  
  .volume-slider {
    width: 60px;
  }
  
  .visualizer-container {
    height: 40px;
  }
  
  .error-message {
    position: static;
    transform: none;
    margin-top: 8px;
    justify-content: center;
  }
}
</style>